import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import apiClient from "../apiClient";
import type {
  CustomerReceivablesRow,
  InvoiceReceivablesRow,
  RiskLevelFilters,
  StatusFilter,
  StatusFilters,
  StatusTile,
  EscalationTile,
  ViewBy,
  InvoiceTypeFilter,
  EscalationFilter,
  EmailActivityFilter,
  ReceivablesEmailComposeContext,
  ReceivablesEmailThreadRow,
} from "../../components/receivables/types";
import type { EmailAttachmentMeta, EmailThread } from "../../utils/interfaces";
import {
  getEmailAttachmentErrorMessage,
  resolveAttachmentMimeType,
} from "../../utils/emailAttachments";
import settings from "../../settings";
import type { IntentionStatus } from "../../utils/types";
import { displayIntentionStatus } from "../../utils/display";
import {
  mapApiAgingBuckets,
  type AgingBucket,
} from "../../utils/receivablesAgingBuckets";

type PaginatedResponse<T> = {
  items: T[];
  total: number;
  skip: number;
  limit: number;
};

type ReceivablesInvoicesPaginatedResponse<T> = PaginatedResponse<T> & {
  total_amount: number;
};

type ReceivablesSummaryApi = {
  open_balance: number;
  unpaid_invoice_count: number;
  critical_amount: number;
  critical_invoice_count: number;
  currency: string;
  available_currencies?: string[];
  status_tiles: Array<{
    key: string;
    label: string;
    count: number;
    amount: number;
  }>;
  escalation_tiles?: Array<{
    key: string;
    label: string;
    count: number;
    amount: number;
  }>;
  aging_buckets: Array<{ name: string; amount: number }>;
  unscoped_aging_buckets?: Array<{ name: string; amount: number }> | null;
  unscoped_open_balance?: number | null;
  unscoped_critical_amount?: number | null;
  unscoped_critical_invoice_count?: number | null;
};

type ReceivablesInvoiceRowApi = {
  external_id: string;
  customer_id: number;
  customer_name: string;
  accounting_software_id: string;
  due_date: string;
  amount: number;
  currency: string;
  overdue_days: number;
  reminded_date: string;
  status: IntentionStatus;
  notes: string;
  risk_level: "current" | "0_30" | "31_60" | "61_90" | "90_plus";
  has_unread_correspondence: boolean;
  has_draft?: boolean;
  is_email_sending_enabled: boolean;
  is_escalated?: boolean;
  escalation_pending_approval?: boolean;
  escalation_step_label?: string | null;
  days_until_next_escalation?: number | null;
  primary_email: string;
  primary_phone: string | null;
};

type ReceivablesCustomerRowApi = {
  customer_id: number;
  customer_name: string;
  accounting_software_id: string;
  currency: string;
  overdue_days: number;
  last_follow_up: string;
  invoices: number;
  notes: string;
  risk_level: "current" | "0_30" | "31_60" | "61_90" | "90_plus";
  current_amount: number;
  one_to_thirty_days_amount: number;
  thirty_one_to_sixty_days_amount: number;
  sixty_one_to_ninety_days_amount: number;
  ninety_one_or_more_days_amount: number;
  total_open_balance: number;
  statuses: IntentionStatus[];
  has_unread_correspondence: boolean;
  is_template_sending: boolean;
  primary_email: string;
  primary_phone: string | null;
};

type ReceivablesManagerFilters = {
  collectorId?: number;
  customerGroupId?: number;
};

export type EmailStatsPeriodGrouping = "day" | "week" | "month";

export type ReceivablesEmailStatsSendVolumePoint = {
  period: string;
  sent: number;
  automatedSent: number;
  manualSent: number;
};

export type ReceivablesEmailStatsReplyVolumePoint = {
  period: string;
  replies: number;
};

export type ReceivablesEmailSenderStat = {
  userId: number;
  username: string;
  emailCount: number;
};

export type ReceivablesEmailUnrespondingCustomer = {
  customerId: number;
  name: string;
};

export type ReceivablesEmailStats = {
  customersEmailed: number;
  customersReplied: number;
  replyRate: number;
  totalReplies: number;
  automatedSent: number;
  manualSent: number;
  periodGrouping: EmailStatsPeriodGrouping;
  senderStats: ReceivablesEmailSenderStat[];
  sendVolume: ReceivablesEmailStatsSendVolumePoint[];
  replyVolume: ReceivablesEmailStatsReplyVolumePoint[];
  unrespondingCustomers: ReceivablesEmailUnrespondingCustomer[];
};

/** Wire shape for GET /receivables/email-stats (design contract). */
type ReceivablesEmailStatsApi = {
  customersEmailed: number;
  customersReplied: number;
  replyRate: number;
  totalReplies?: number;
  automatedSent?: number;
  manualSent?: number;
  periodGrouping: EmailStatsPeriodGrouping;
  senderStats: ReceivablesEmailSenderStat[];
  sendVolume: Array<{
    period: string;
    sent: number;
    automatedSent?: number;
    manualSent?: number;
  }>;
  replyVolume?: Array<{ period: string; replies: number }>;
  unrespondingCustomers?: ReceivablesEmailUnrespondingCustomer[];
};

function appendManagerFilters(
  params: URLSearchParams,
  managerFilters?: ReceivablesManagerFilters,
) {
  if (managerFilters?.collectorId != null) {
    params.set("collector_id", String(managerFilters.collectorId));
  }
  if (managerFilters?.customerGroupId != null) {
    params.set("customer_group_id", String(managerFilters.customerGroupId));
  }
}

export function buildReceivablesEmailStatsParams({
  startDate,
  endDate,
  periodGrouping,
  managerFilters,
}: {
  startDate?: string;
  endDate?: string;
  periodGrouping: EmailStatsPeriodGrouping;
  managerFilters?: ReceivablesManagerFilters;
}): URLSearchParams {
  const params = new URLSearchParams();
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  params.set("period_grouping", periodGrouping);
  appendManagerFilters(params, managerFilters);
  return params;
}

export function mapEmailStats(
  data: ReceivablesEmailStatsApi,
): ReceivablesEmailStats {
  return {
    customersEmailed: data.customersEmailed,
    customersReplied: data.customersReplied,
    replyRate: data.replyRate,
    totalReplies: data.totalReplies ?? 0,
    automatedSent: data.automatedSent ?? 0,
    manualSent: data.manualSent ?? 0,
    periodGrouping: data.periodGrouping,
    senderStats: data.senderStats ?? [],
    sendVolume: (data.sendVolume ?? []).map((point) => ({
      period: point.period,
      sent: point.sent,
      automatedSent: point.automatedSent ?? 0,
      manualSent: point.manualSent ?? 0,
    })),
    replyVolume: (data.replyVolume ?? []).map((point) => ({
      period: point.period,
      replies: point.replies,
    })),
    unrespondingCustomers: (data.unrespondingCustomers ?? []).map(
      (customer) => ({
        customerId: customer.customerId,
        name: customer.name,
      }),
    ),
  };
}

export const receivablesKeys = {
  all: ["receivables"] as const,
  summary: (
    currency: string,
    startDate?: string,
    endDate?: string,
    managerFilters?: ReceivablesManagerFilters,
    consolidated?: boolean,
  ) =>
    [
      ...receivablesKeys.all,
      "summary",
      currency,
      startDate,
      endDate,
      managerFilters,
      consolidated ?? false,
    ] as const,
  emailStats: (
    startDate?: string,
    endDate?: string,
    periodGrouping: EmailStatsPeriodGrouping = "week",
    managerFilters?: ReceivablesManagerFilters,
  ) =>
    [
      ...receivablesKeys.all,
      "emailStats",
      startDate,
      endDate,
      periodGrouping,
      managerFilters,
    ] as const,
  invoices: (params: Record<string, unknown>) =>
    [...receivablesKeys.all, "invoices", params] as const,
  customers: (params: Record<string, unknown>) =>
    [...receivablesKeys.all, "customers", params] as const,
  emails: (params: Record<string, unknown>) =>
    [...receivablesKeys.all, "emails", params] as const,
  emailThread: (gmailThreadId: string) =>
    [...receivablesKeys.all, "emailThread", gmailThreadId] as const,
  emailComposeContext: (gmailThreadId: string, preferEmail?: string) =>
    [
      ...receivablesKeys.all,
      "emailComposeContext",
      gmailThreadId,
      preferEmail ?? "",
    ] as const,
  emailAttachments: (messageId: string) =>
    [...receivablesKeys.all, "emailAttachments", messageId] as const,
  groupAgingBreakdown: (
    currency: string,
    consolidated?: boolean,
    managerFilters?: ReceivablesManagerFilters,
  ) =>
    [
      ...receivablesKeys.all,
      "groupAgingBreakdown",
      currency,
      consolidated ?? false,
      managerFilters,
    ] as const,
};

type ReceivablesEmailThreadRowApi = {
  gmail_thread_id: string;
  subject: string;
  from_name: string;
  from_email: string;
  snippet: string;
  last_message_at: string;
  message_count: number;
  has_unread: boolean;
  linked_invoice_ids: string[];
  linked_customer_names: string[];
  linked_customer_organization_id?: number | null;
  has_payment_link: boolean;
  last_message_is_outgoing: boolean;
  sent_by_user_id?: number | null;
  sent_by_username?: string | null;
  send_source?: string | null;
};

type ReceivablesEmailComposeContextApi = {
  customer_organization_id: number | null;
  customer_name: string | null;
  open_invoice_external_ids: string[];
};

function mapEmailThreadRow(
  row: ReceivablesEmailThreadRowApi,
): ReceivablesEmailThreadRow {
  return {
    gmailThreadId: row.gmail_thread_id,
    subject: row.subject,
    fromName: row.from_name,
    fromEmail: row.from_email,
    snippet: row.snippet,
    lastMessageAt: row.last_message_at,
    messageCount: row.message_count,
    hasUnread: row.has_unread,
    linkedInvoiceIds: row.linked_invoice_ids,
    linkedCustomerNames: row.linked_customer_names,
    linkedCustomerRepId: row.linked_customer_organization_id ?? null,
    hasPaymentLink: row.has_payment_link,
    lastMessageIsOutgoing: row.last_message_is_outgoing,
    sentByUserId: row.sent_by_user_id ?? null,
    sentByUsername: row.sent_by_username ?? null,
    sendSource: row.send_source ?? null,
  };
}

function mapEmailComposeContext(
  data: ReceivablesEmailComposeContextApi,
): ReceivablesEmailComposeContext {
  return {
    customerRepId: data.customer_organization_id,
    customerName: data.customer_name,
    openInvoiceExternalIds: data.open_invoice_external_ids,
  };
}

type ReceivablesGroupAgingBreakdownApi = {
  group_id: number;
  group_name: string;
  open_balance: number;
  aging_buckets: Array<{ name: string; amount: number }>;
};

export type ReceivablesGroupAgingBreakdown = {
  groupId: number;
  groupName: string;
  openBalance: number;
  agingBuckets: AgingBucket[];
};

function iconForStatusTile(key: StatusFilter): StatusTile["icon"] {
  if (key === "all") return "list";
  if (key === "Approved") return "approved";
  if (key === "In_progress") return "inProgress";
  if (key === "Unresolved") return "unresolved";
  if (key === "Needs_attention") return "needsAttention";
  return "noReply";
}

function mapSummaryToStatusTiles(
  tiles: ReceivablesSummaryApi["status_tiles"],
): StatusTile[] {
  return tiles.map((tile) => ({
    key: tile.key as StatusFilter,
    label:
      tile.key === "all"
        ? tile.label
        : (displayIntentionStatus(tile.key as IntentionStatus) ?? tile.label),
    count: tile.count,
    amount: tile.amount,
    icon: iconForStatusTile(tile.key as StatusFilter),
  }));
}

function mapSummaryToEscalatedTile(
  tiles: ReceivablesSummaryApi["escalation_tiles"],
): EscalationTile {
  const escalated = tiles?.find((tile) => tile.key === "escalated");
  return {
    key: "escalated",
    label: escalated?.label ?? "Escalated",
    count: escalated?.count ?? 0,
    amount: escalated?.amount ?? 0,
    icon: "escalated",
  };
}

function mapInvoiceRow(row: ReceivablesInvoiceRowApi): InvoiceReceivablesRow {
  const dueDate = new Date(row.due_date);
  const dueDateLabel = Number.isNaN(dueDate.getTime())
    ? "-"
    : dueDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

  return {
    externalId: row.external_id,
    customerId: row.customer_id,
    customerName: row.customer_name,
    accountingSoftwareId: row.accounting_software_id,
    dueDate: dueDateLabel,
    amount: row.amount,
    currency: row.currency,
    overdueDays: row.overdue_days,
    remindedDate: row.reminded_date,
    status: row.status,
    notes: row.notes,
    riskLevel: row.risk_level,
    hasUnreadCorrespondence: row.has_unread_correspondence,
    isEmailSendingEnabled: row.is_email_sending_enabled,
    isEscalated: row.is_escalated ?? false,
    escalationPendingApproval: row.escalation_pending_approval ?? false,
    escalationStepLabel: row.escalation_step_label ?? null,
    daysUntilNextEscalation: row.days_until_next_escalation ?? null,
    primaryEmail: row.primary_email,
    primaryPhone: row.primary_phone,
    hasDraft: row.has_draft ?? false,
  };
}

function mapCustomerRow(
  row: ReceivablesCustomerRowApi,
): CustomerReceivablesRow {
  return {
    customerId: row.customer_id,
    customerName: row.customer_name,
    accountingSoftwareId: row.accounting_software_id,
    currency: row.currency,
    overdueDays: row.overdue_days,
    lastFollowUp: row.last_follow_up,
    invoices: row.invoices,
    notes: row.notes,
    riskLevel: row.risk_level,
    currentAmount: row.current_amount,
    oneToThirtyDaysAmount: row.one_to_thirty_days_amount,
    thirtyOneToSixtyDaysAmount: row.thirty_one_to_sixty_days_amount,
    sixtyOneToNinetyDaysAmount: row.sixty_one_to_ninety_days_amount,
    ninetyOneOrMoreDaysAmount: row.ninety_one_or_more_days_amount,
    totalOpenBalance: row.total_open_balance,
    statuses: row.statuses,
    hasUnreadCorrespondence: row.has_unread_correspondence,
    isTemplateSending: row.is_template_sending,
    primaryEmail: row.primary_email,
    primaryPhone: row.primary_phone,
  };
}

export { mapCustomerRow, mapInvoiceRow };

export function buildTableParams({
  page,
  pageSize,
  currency,
  consolidated,
  search,
  riskLevels,
  statusFilters,
  sorting,
  startDate,
  endDate,
  managerFilters,
  invoiceTypeFilter,
  escalationFilter,
  emailActivityFilter,
}: {
  page: number;
  pageSize: number;
  currency: string;
  consolidated?: boolean;
  search?: string;
  riskLevels: RiskLevelFilters;
  statusFilters: StatusFilters;
  sorting: SortingState;
  startDate?: string;
  endDate?: string;
  managerFilters?: ReceivablesManagerFilters;
  invoiceTypeFilter?: InvoiceTypeFilter;
  escalationFilter?: EscalationFilter;
  emailActivityFilter?: EmailActivityFilter;
}) {
  const sort = sorting[0];
  const params = new URLSearchParams();
  params.set("skip", String((page - 1) * pageSize));
  params.set("limit", String(pageSize));
  params.set("currency", currency);
  if (consolidated) params.set("consolidated", "true");
  if (search) params.set("search", search);
  for (const level of riskLevels) {
    params.append("risk_level", level);
  }
  if (invoiceTypeFilter && invoiceTypeFilter !== "all") {
    params.set("invoice_prefix", invoiceTypeFilter);
  }
  if (emailActivityFilter && emailActivityFilter !== "all") {
    params.set("activity_filter", emailActivityFilter);
  }
  if (escalationFilter && escalationFilter !== "all") {
    params.set("escalation_filter", escalationFilter);
  } else {
    for (const status of statusFilters) {
      params.append("intention_status", status);
    }
  }
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  appendManagerFilters(params, managerFilters);
  if (sort?.id) {
    params.set("sort_by", sort.id);
    params.set("sort_dir", sort.desc ? "desc" : "asc");
  }
  return params;
}

function tableQueryParamsFromSearchParams(
  params: URLSearchParams,
): Record<string, string> {
  const queryParams = Object.fromEntries(params.entries());
  const riskLevels = params
    .getAll("risk_level")
    .slice()
    .sort()
    .join(",");
  if (riskLevels) {
    queryParams.risk_level = riskLevels;
  } else {
    delete queryParams.risk_level;
  }
  const intentionStatuses = params
    .getAll("intention_status")
    .slice()
    .sort()
    .join(",");
  if (intentionStatuses) {
    queryParams.intention_status = intentionStatuses;
  } else {
    delete queryParams.intention_status;
  }
  return queryParams;
}

const RECEIVABLES_TABLE_FILTER_PARAM_KEYS = [
  "currency",
  "consolidated",
  "search",
  "risk_level",
  "invoice_prefix",
  "escalation_filter",
  "intention_status",
  "start_date",
  "end_date",
  "collector_id",
  "customer_group_id",
  "sort_by",
  "sort_dir",
] as const;

function receivablesTableFiltersMatch(
  previous: Record<string, string>,
  current: Record<string, string>,
): boolean {
  return RECEIVABLES_TABLE_FILTER_PARAM_KEYS.every(
    (key) => (previous[key] ?? "") === (current[key] ?? ""),
  );
}

export function useReceivablesSummaryQuery(
  currency: string,
  startDate?: string,
  endDate?: string,
  managerFilters?: ReceivablesManagerFilters,
  consolidated?: boolean,
) {
  return useQuery({
    queryKey: receivablesKeys.summary(
      currency,
      startDate,
      endDate,
      managerFilters,
      consolidated,
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("currency", currency);
      if (consolidated) params.set("consolidated", "true");
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      appendManagerFilters(params, managerFilters);
      const { data } = await apiClient.get<ReceivablesSummaryApi>(
        `/receivables/summary?${params.toString()}`,
      );
      return {
        openBalance: data.open_balance,
        unpaidInvoicesCount: data.unpaid_invoice_count,
        criticalAmount: data.critical_amount,
        criticalInvoicesCount: data.critical_invoice_count,
        currency: data.currency,
        availableCurrencies: data.available_currencies ?? [],
        statusTiles: mapSummaryToStatusTiles(data.status_tiles),
        escalationTile: mapSummaryToEscalatedTile(data.escalation_tiles),
        agingBuckets: mapApiAgingBuckets(data.aging_buckets),
        unscopedAgingBuckets: data.unscoped_aging_buckets
          ? mapApiAgingBuckets(data.unscoped_aging_buckets)
          : undefined,
        unscopedOpenBalance: data.unscoped_open_balance ?? undefined,
        unscopedCriticalAmount: data.unscoped_critical_amount ?? undefined,
        unscopedCriticalInvoiceCount:
          data.unscoped_critical_invoice_count ?? undefined,
      };
    },
    enabled: Boolean(currency),
  });
}

export function useReceivablesGroupAgingBreakdownQuery({
  currency,
  consolidated,
  managerFilters,
  enabled = true,
}: {
  currency: string;
  consolidated?: boolean;
  managerFilters?: ReceivablesManagerFilters;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: receivablesKeys.groupAgingBreakdown(
      currency,
      consolidated,
      managerFilters,
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("currency", currency);
      if (consolidated) params.set("consolidated", "true");
      appendManagerFilters(params, managerFilters);
      const { data } = await apiClient.get<ReceivablesGroupAgingBreakdownApi[]>(
        `/receivables/group-aging-breakdown?${params.toString()}`,
      );
      return data.map((group) => ({
        groupId: group.group_id,
        groupName: group.group_name,
        openBalance: group.open_balance,
        agingBuckets: mapApiAgingBuckets(group.aging_buckets),
      }));
    },
    enabled: enabled && Boolean(currency),
  });
}

export function useReceivablesEmailStatsQuery({
  startDate,
  endDate,
  periodGrouping,
  managerFilters,
  enabled = true,
}: {
  startDate?: string;
  endDate?: string;
  periodGrouping: EmailStatsPeriodGrouping;
  managerFilters?: ReceivablesManagerFilters;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: receivablesKeys.emailStats(
      startDate,
      endDate,
      periodGrouping,
      managerFilters,
    ),
    queryFn: async () => {
      const params = buildReceivablesEmailStatsParams({
        startDate,
        endDate,
        periodGrouping,
        managerFilters,
      });
      const { data } = await apiClient.get<ReceivablesEmailStatsApi>(
        `/receivables/email-stats?${params.toString()}`,
      );
      return mapEmailStats(data);
    },
    enabled: enabled && Boolean(startDate && endDate),
  });
}

export function useReceivablesInvoicesQuery({
  page,
  pageSize,
  currency,
  consolidated,
  search,
  riskLevels,
  statusFilters,
  sorting,
  startDate,
  endDate,
  managerFilters,
  invoiceTypeFilter,
  escalationFilter,
  emailActivityFilter,
  enabled = true,
}: {
  page: number;
  pageSize: number;
  currency: string;
  consolidated?: boolean;
  search?: string;
  riskLevels: RiskLevelFilters;
  statusFilters: StatusFilters;
  sorting: SortingState;
  startDate?: string;
  endDate?: string;
  managerFilters?: ReceivablesManagerFilters;
  invoiceTypeFilter?: InvoiceTypeFilter;
  escalationFilter?: EscalationFilter;
  emailActivityFilter?: EmailActivityFilter;
  enabled?: boolean;
}) {
  const params = buildTableParams({
    page,
    pageSize,
    currency,
    consolidated,
    search,
    riskLevels,
    statusFilters,
    sorting,
    startDate,
    endDate,
    managerFilters,
    invoiceTypeFilter,
    escalationFilter,
    emailActivityFilter,
  });
  const queryParams = tableQueryParamsFromSearchParams(params);

  return useQuery({
    queryKey: receivablesKeys.invoices(queryParams),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<
        ReceivablesInvoicesPaginatedResponse<ReceivablesInvoiceRowApi>
      >(`/receivables/invoices?${params.toString()}`, { signal });
      return {
        rows: data.items.map(mapInvoiceRow),
        total: data.total,
        totalAmount: data.total_amount,
      };
    },
    enabled: enabled && Boolean(currency),
    placeholderData: (previousData, previousQuery) => {
      if (!previousData || !previousQuery) return undefined;
      const previousParams = previousQuery.queryKey[2];
      if (
        typeof previousParams !== "object" ||
        previousParams == null ||
        !receivablesTableFiltersMatch(
          previousParams as Record<string, string>,
          queryParams,
        )
      ) {
        return undefined;
      }
      return previousData;
    },
  });
}

export function useReceivablesCustomersQuery({
  page,
  pageSize,
  currency,
  consolidated,
  search,
  riskLevels,
  statusFilters,
  sorting,
  startDate,
  endDate,
  managerFilters,
  invoiceTypeFilter,
  enabled = true,
}: {
  page: number;
  pageSize: number;
  currency: string;
  consolidated?: boolean;
  search?: string;
  riskLevels: RiskLevelFilters;
  statusFilters: StatusFilters;
  sorting: SortingState;
  startDate?: string;
  endDate?: string;
  managerFilters?: ReceivablesManagerFilters;
  invoiceTypeFilter?: InvoiceTypeFilter;
  enabled?: boolean;
}) {
  const params = buildTableParams({
    page,
    pageSize,
    currency,
    consolidated,
    search,
    riskLevels,
    statusFilters,
    sorting,
    startDate,
    endDate,
    managerFilters,
    invoiceTypeFilter,
  });
  const queryParams = tableQueryParamsFromSearchParams(params);

  return useQuery({
    queryKey: receivablesKeys.customers(queryParams),
    queryFn: async () => {
      const { data } = await apiClient.get<
        PaginatedResponse<ReceivablesCustomerRowApi>
      >(`/receivables/customers?${params.toString()}`);
      return {
        rows: data.items.map(mapCustomerRow),
        total: data.total,
      };
    },
    enabled: enabled && Boolean(currency),
    placeholderData: keepPreviousData,
  });
}

export function useReceivablesEmailsQuery({
  page,
  pageSize,
  search,
  direction = "incoming",
  sentBy,
  enabled = true,
}: {
  page: number;
  pageSize: number;
  search?: string;
  direction?: "incoming" | "outgoing" | "all";
  sentBy?: number | "automated";
  enabled?: boolean;
}) {
  const params = new URLSearchParams();
  params.set("skip", String((page - 1) * pageSize));
  params.set("limit", String(pageSize));
  if (search) params.set("search", search);
  if (direction !== "all") params.set("direction", direction);
  if (sentBy !== undefined) {
    params.set(
      "sent_by",
      sentBy === "automated" ? "automated" : String(sentBy),
    );
  }
  params.set("sort_by", "last_message_at");
  params.set("sort_dir", "desc");

  return useQuery({
    queryKey: receivablesKeys.emails({
      page,
      pageSize,
      search,
      direction,
      sentBy,
    }),
    queryFn: async () => {
      const { data } = await apiClient.get<
        PaginatedResponse<ReceivablesEmailThreadRowApi>
      >(`/receivables/emails?${params.toString()}`);
      return {
        rows: data.items.map(mapEmailThreadRow),
        total: data.total,
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useReceivablesEmailComposeContextQuery({
  gmailThreadId,
  preferEmail,
  enabled = true,
}: {
  gmailThreadId: string | null;
  preferEmail?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: receivablesKeys.emailComposeContext(
      gmailThreadId ?? "",
      preferEmail,
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (preferEmail) {
        params.set("prefer_email", preferEmail);
      }
      const qs = params.toString();
      const { data } = await apiClient.get<ReceivablesEmailComposeContextApi>(
        `/receivables/emails/${encodeURIComponent(gmailThreadId!)}/compose_context${qs ? `?${qs}` : ""}`,
      );
      return mapEmailComposeContext(data);
    },
    enabled: enabled && Boolean(gmailThreadId),
  });
}

export function useReceivablesEmailThreadQuery(gmailThreadId: string | null) {
  return useQuery({
    queryKey: receivablesKeys.emailThread(gmailThreadId ?? ""),
    queryFn: async () => {
      const { data } = await apiClient.get<EmailThread>(
        `/receivables/emails/${encodeURIComponent(gmailThreadId!)}`,
      );
      return data;
    },
    enabled: Boolean(gmailThreadId),
  });
}

export type EmailAttachmentDisposition = "inline" | "attachment";

function getApiBaseUrl(): string {
  const configured = settings.baseUrl?.trim();
  if (configured) {
    return configured.replace(/\/$/, "");
  }
  return "/api/v1";
}

export function buildEmailAttachmentContentUrl(
  messageId: string,
  attachmentId: string,
  disposition: EmailAttachmentDisposition = "inline",
): string {
  const path = `${getApiBaseUrl()}/receivables/emails/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
  return `${path}?disposition=${disposition}`;
}

function filenameFromContentDisposition(
  contentDisposition: string | undefined,
): string | null {
  if (!contentDisposition) return null;
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1]?.trim() || null;
}

function attachmentBlobFromResponse(
  data: Blob,
  headers: Record<string, unknown>,
  filenameHint: string,
): Blob {
  const headerType =
    typeof headers["content-type"] === "string"
      ? headers["content-type"]
      : undefined;
  const filename =
    filenameFromContentDisposition(
      headers["content-disposition"] as string | undefined,
    ) ?? filenameHint;
  const type = resolveAttachmentMimeType(headerType, filename);
  if (data.type === type) {
    return data;
  }
  return new Blob([data], { type });
}

export class EmailAttachmentFetchError extends Error {
  status: number;
  reconnectMailbox: boolean;

  constructor(
    message: string,
    status: number,
    options?: { reconnectMailbox?: boolean },
  ) {
    super(message);
    this.name = "EmailAttachmentFetchError";
    this.status = status;
    this.reconnectMailbox = options?.reconnectMailbox ?? false;
  }
}

async function readBlobErrorDetail(data: unknown): Promise<string | null> {
  if (!(data instanceof Blob)) return null;
  try {
    const text = await data.text();
    if (!text.trim()) return null;
    const parsed = JSON.parse(text) as { detail?: unknown };
    if (typeof parsed.detail === "string" && parsed.detail.trim()) {
      return parsed.detail;
    }
    if (
      parsed.detail &&
      typeof parsed.detail === "object" &&
      "message" in parsed.detail
    ) {
      const message = (parsed.detail as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) {
        return message;
      }
    }
    return text;
  } catch {
    return null;
  }
}

export async function fetchEmailAttachmentBlob(
  messageId: string,
  attachmentId: string,
  disposition: EmailAttachmentDisposition = "inline",
  signal?: AbortSignal,
  filenameHint = "attachment",
): Promise<Blob> {
  try {
    const response = await apiClient.get(
      `/receivables/emails/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
      {
        params: { disposition },
        responseType: "blob",
        signal,
      },
    );
    return attachmentBlobFromResponse(
      response.data as Blob,
      response.headers as Record<string, unknown>,
      filenameHint,
    );
  } catch (error) {
    const response = (
      error as { response?: { status?: number; data?: unknown } }
    ).response;
    const status = response?.status ?? 500;
    const detailMessage = await readBlobErrorDetail(response?.data);
    const { message, reconnectMailbox } = getEmailAttachmentErrorMessage(
      status,
      detailMessage,
    );
    throw new EmailAttachmentFetchError(message, status, {
      reconnectMailbox,
    });
  }
}

type EmailAttachmentsListApi = {
  attachments: Record<string, EmailAttachmentMeta>;
};

export function useEmailAttachmentsListQuery({
  messageId,
  enabled = true,
}: {
  messageId: string | null;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: receivablesKeys.emailAttachments(messageId ?? ""),
    queryFn: async () => {
      const { data } = await apiClient.get<EmailAttachmentsListApi>(
        `/receivables/emails/messages/${encodeURIComponent(messageId!)}/attachments`,
      );
      return data.attachments;
    },
    enabled: enabled && Boolean(messageId),
    staleTime: 60_000,
  });
}

export function useMarkReceivablesThreadReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (gmailThreadId: string) => {
      await apiClient.post(
        `/receivables/emails/${encodeURIComponent(gmailThreadId)}/mark_read`,
      );
    },
    onSuccess: (_data, gmailThreadId) => {
      void queryClient.invalidateQueries({ queryKey: receivablesKeys.all });
      void queryClient.invalidateQueries({
        queryKey: receivablesKeys.emailThread(gmailThreadId),
      });
    },
  });
}

export async function exportReceivablesCsv({
  viewBy,
  currency,
  consolidated,
  search,
  riskLevels,
  statusFilters,
  sorting,
  startDate,
  endDate,
  managerFilters,
  invoiceTypeFilter,
  escalationFilter,
}: {
  viewBy: ViewBy;
  currency: string;
  consolidated?: boolean;
  search?: string;
  riskLevels: RiskLevelFilters;
  statusFilters: StatusFilters;
  sorting: SortingState;
  startDate?: string;
  endDate?: string;
  managerFilters?: ReceivablesManagerFilters;
  invoiceTypeFilter?: InvoiceTypeFilter;
  escalationFilter?: EscalationFilter;
}) {
  const params = buildTableParams({
    page: 1,
    pageSize: 10,
    currency,
    consolidated,
    search,
    riskLevels,
    statusFilters,
    sorting,
    startDate,
    endDate,
    managerFilters,
    invoiceTypeFilter,
    escalationFilter: viewBy === "invoices" ? escalationFilter : "all",
  });
  params.delete("skip");
  params.delete("limit");
  params.set("view_by", viewBy);

  const response = await apiClient.get(
    `/receivables/export?${params.toString()}`,
    {
      responseType: "blob",
    },
  );
  const contentType =
    (response.headers["content-type"] as string | undefined) ??
    "text/csv;charset=utf-8";
  const disposition =
    (response.headers["content-disposition"] as string | undefined) ?? "";
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] ?? "receivables.csv";
  const blob = new Blob([response.data], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
